/**
 * Complementary Bundle - Standalone Section
 *
 * Checkbox-based bundle functionality for complementary products.
 * Updates the main product price display and intercepts Add to Cart.
 *
 * FILES NEEDED:
 * 1. sections/complementary-bundle.liquid
 * 2. assets/complementary-bundle.js (this file)
 * 3. assets/component-complementary-bundle.css
 */

if (!customElements.get('complementary-bundle')) {
  class ComplementaryBundle extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
    }

    connectedCallback() {
      // Lazy load with IntersectionObserver
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.fetchRecommendations();
      };

      new IntersectionObserver(handleIntersection.bind(this), {
        rootMargin: '0px 0px 400px 0px'
      }).observe(this);
    }

    fetchRecommendations() {
      if (this.loaded) return;

      const url = `${this.dataset.url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`;

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('complementary-bundle');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
            this.loaded = true;
            this.init();
          } else {
            this.remove();
          }
        })
        .catch((e) => {
          console.error('ComplementaryBundle fetch error:', e);
        });
    }

    init() {
      this.checkboxes = this.querySelectorAll('.complementary-bundle__checkbox');
      this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
      this.mainComparePrice = parseInt(this.dataset.mainComparePrice, 10) || 0;
      this.mainVariantId = this.dataset.mainVariantId;
      this.sectionId = this.dataset.sectionId;

      // Store original prices for reset
      this.originalMainPrice = this.mainPrice;
      this.originalMainComparePrice = this.mainComparePrice;

      // Find the main product price container
      this.findPriceElements();

      // Bind checkbox change events
      this.checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', this.onCheckboxChange.bind(this));
      });

      // Find and intercept the main product form
      this.interceptProductForm();

      // Listen for variant changes to update base price
      this.listenForVariantChanges();

      // Listen for quantity changes to update price
      this.listenForQuantityChanges();
    }

    getQuantity() {
      const ctaInput = document.querySelector('.sticky-cta__quantity .quantity__input');
      const mainInput = document.querySelector('product-form input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]');
      const input = ctaInput || mainInput;
      return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
    }

    listenForQuantityChanges() {
      const handler = () => this.updateProductPrice();
      const ctaInput = document.querySelector('.sticky-cta__quantity .quantity__input');
      const mainInput = document.querySelector('product-form input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]');

      [ctaInput, mainInput].filter(Boolean).forEach((input) => {
        input.addEventListener('change', handler);
        input.addEventListener('input', handler);
      });
    }

    findPriceElements() {
      // Find the product section/container
      const productContainer = document.querySelector('.section-main-product, .product, [data-section-type="product"]');

      if (!productContainer) return;

      // Try multiple selectors to find the price container
      const priceSelectors = [
        '[id^="price-"] .price',
        '.product__price .price',
        '.product__info-wrapper .price',
        '.price__container'
      ];

      for (const selector of priceSelectors) {
        const priceEl = productContainer.querySelector(selector);
        if (priceEl) {
          this.priceContainer = priceEl.closest('[id^="price-"]') || priceEl.closest('.product__price') || priceEl;
          break;
        }
      }

      if (this.priceContainer) {
        // Find the actual price display elements
        // Note: The compare/regular price in sale section uses <s class="price-item--regular"> (strikethrough)
        this.priceElements = {
          regular: this.priceContainer.querySelector('.price__regular .price-item--regular'),
          sale: this.priceContainer.querySelector('.price__sale .price-item--sale'),
          saleLast: this.priceContainer.querySelector('.price__sale .price-item--last'),
          allLast: this.priceContainer.querySelectorAll('.price-item--last'),
          // The compare price (strikethrough) is in .price__sale as s.price-item--regular
          saleCompare: this.priceContainer.querySelector('.price__sale s.price-item--regular'),
          allSaleCompare: this.priceContainer.querySelectorAll('.price__sale s.price-item--regular')
        };

        // Store original price text for potential reset
        if (this.priceElements.saleLast) {
          this.originalPriceText = this.priceElements.saleLast.textContent.trim();
        } else if (this.priceElements.regular) {
          this.originalPriceText = this.priceElements.regular.textContent.trim();
        }

        // Store original compare price text
        if (this.priceElements.saleCompare) {
          this.originalComparePriceText = this.priceElements.saleCompare.textContent.trim();
        }
      }

      // Find sticky CTA price elements
      const ctaPriceEl = document.querySelector('.sticky-cta__price .price');
      if (ctaPriceEl) {
        this.ctaPriceElements = {
          regular: ctaPriceEl.querySelector('.price__regular .price-item--regular'),
          sale: ctaPriceEl.querySelector('.price__sale .price-item--sale'),
          saleLast: ctaPriceEl.querySelector('.price__sale .price-item--last'),
          allLast: ctaPriceEl.querySelectorAll('.price-item--last'),
          saleCompare: ctaPriceEl.querySelector('.price__sale s.price-item--regular'),
          allSaleCompare: ctaPriceEl.querySelectorAll('.price__sale s.price-item--regular')
        };
      }

      this.mainTotalLabel = this.priceContainer ? this.priceContainer.querySelector('.product-price__total-label') : null;
      this.ctaTotalLabel = document.querySelector('.sticky-cta__total-label');
    }

    listenForVariantChanges() {
      // Listen for custom variant change events
      document.addEventListener('variant:changed', (event) => {
        if (event.detail && event.detail.variant) {
          this.mainPrice = event.detail.variant.price;
          this.mainComparePrice = event.detail.variant.compare_at_price || 0;
          this.originalMainPrice = this.mainPrice;
          this.originalMainComparePrice = this.mainComparePrice;
          this.updateProductPrice();
        }
      });

      // Listen for Shopify native variant selects
      const variantSelects = document.querySelector('variant-selects, variant-radios');
      if (variantSelects) {
        variantSelects.addEventListener('change', () => {
          setTimeout(() => this.refreshMainPrice(), 150);
        });
      }

      // Also listen on the product form for variant input changes
      const variantInput = document.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          setTimeout(() => this.refreshMainPrice(), 150);
        });
        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    refreshMainPrice() {
      // Re-find price elements in case DOM changed
      this.findPriceElements();

      // Try to read from the price element
      if (this.priceElements && this.priceElements.saleLast) {
        const priceText = this.priceElements.saleLast.textContent;
        const parsed = this.parsePriceFromText(priceText);
        if (parsed && parsed !== this.mainPrice) {
          this.mainPrice = parsed;
          this.originalMainPrice = parsed;
        }
      }

      this.updateProductPrice();
    }

    parsePriceFromText(text) {
      // Parse price from text like "€17,35 EUR" or "€17,35" or "€1.234,56"
      if (!text) return null;

      // Remove currency symbols and "EUR"
      let cleaned = text.replace(/[€$£]/g, '').replace(/EUR/gi, '').trim();

      // Handle European format: 1.234,56 -> 1234.56
      if (cleaned.includes(',')) {
        // Check if comma is decimal separator (European format)
        const parts = cleaned.split(',');
        if (parts.length === 2 && parts[1].length <= 2) {
          // European format: remove thousand separators (dots), replace comma with dot
          cleaned = parts[0].replace(/\./g, '') + '.' + parts[1];
        }
      }

      const value = parseFloat(cleaned);
      if (!isNaN(value)) {
        return Math.round(value * 100);
      }
      return null;
    }

    interceptProductForm() {
      // Find the main product form (not inside this component)
      const forms = document.querySelectorAll('product-form form[action*="/cart/add"], form[action*="/cart/add"]');

      forms.forEach(form => {
        // Skip forms inside complementary-bundle
        if (form.closest('complementary-bundle')) return;

        // Check if this is the main product form
        const isMainForm = form.closest('.section-main-product, .product, [data-section-type="product"]');
        if (!isMainForm) return;

        this.productForm = form;
        const productFormElement = form.closest('product-form');

        if (productFormElement && !productFormElement.hasAttribute('data-bundle-intercepted')) {
          productFormElement.setAttribute('data-bundle-intercepted', 'true');
          this.productFormElement = productFormElement;
        } else if (!form.hasAttribute('data-bundle-intercepted')) {
          form.setAttribute('data-bundle-intercepted', 'true');
        }

        // Add submit listener with capture to intercept before other handlers
        form.addEventListener('submit', (e) => this.onFormSubmit(e, form), true);
      });
    }

    onCheckboxChange(event) {
      const checkbox = event.target;
      const item = checkbox.closest('.complementary-bundle__item');

      if (checkbox.checked) {
        item.classList.add('complementary-bundle__item--selected');
      } else {
        item.classList.remove('complementary-bundle__item--selected');
      }

      this.updateProductPrice();
    }

    updateProductPrice() {
      const { total, compareTotal } = this.calculateTotal();
      const formattedPrice = this.formatMoney(total);
      const hasBundleSelected = total !== this.mainPrice;

      // Update the main product price display
      if (this.priceContainer) {
        if (this.priceElements.regular) {
          this.priceElements.regular.textContent = formattedPrice;
        }
        if (this.priceElements.sale) {
          this.priceElements.sale.textContent = formattedPrice;
        }
        if (this.priceElements.saleLast) {
          this.priceElements.saleLast.textContent = formattedPrice;
        }

        if (this.priceElements.allLast) {
          this.priceElements.allLast.forEach(el => {
            el.textContent = formattedPrice;
          });
        }

        if (compareTotal > 0) {
          const formattedComparePrice = this.formatMoney(compareTotal);

          if (this.priceElements.saleCompare) {
            this.priceElements.saleCompare.textContent = formattedComparePrice;
          }

          if (this.priceElements.allSaleCompare) {
            this.priceElements.allSaleCompare.forEach(el => {
              el.textContent = formattedComparePrice;
            });
          }
        }
      }

      if (this.mainTotalLabel) {
        this.mainTotalLabel.classList.toggle('is-visible', hasBundleSelected);
      }

      // Update sticky CTA price and "Totaal:" label
      if (this.ctaTotalLabel) {
        this.ctaTotalLabel.classList.toggle('is-visible', hasBundleSelected);
      }

      if (this.ctaPriceElements) {
        if (this.ctaPriceElements.regular) {
          this.ctaPriceElements.regular.textContent = formattedPrice;
        }
        if (this.ctaPriceElements.sale) {
          this.ctaPriceElements.sale.textContent = formattedPrice;
        }
        if (this.ctaPriceElements.saleLast) {
          this.ctaPriceElements.saleLast.textContent = formattedPrice;
        }
        if (this.ctaPriceElements.allLast) {
          this.ctaPriceElements.allLast.forEach(el => {
            el.textContent = formattedPrice;
          });
        }
        if (compareTotal > 0) {
          const formattedComparePrice = this.formatMoney(compareTotal);
          if (this.ctaPriceElements.saleCompare) {
            this.ctaPriceElements.saleCompare.textContent = formattedComparePrice;
          }
          if (this.ctaPriceElements.allSaleCompare) {
            this.ctaPriceElements.allSaleCompare.forEach(el => {
              el.textContent = formattedComparePrice;
            });
          }
        }
      }
    }

    calculateTotal() {
      const quantity = this.getQuantity();
      let total = this.mainPrice * quantity;
      let compareTotal = this.mainComparePrice * quantity;

      this.checkboxes.forEach(checkbox => {
        if (checkbox.checked) {
          const price = parseInt(checkbox.dataset.price, 10) || 0;
          total += price;

          // Get compare price from the item's data attribute
          const item = checkbox.closest('.complementary-bundle__item');
          const itemComparePrice = parseInt(item?.dataset.comparePrice, 10) || 0;

          // If item has compare price, add it; otherwise add the regular price
          if (itemComparePrice > 0) {
            compareTotal += itemComparePrice;
          } else {
            compareTotal += price;
          }
        }
      });

      return { total, compareTotal };
    }

    formatMoney(cents) {
    const currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
    const currencySymbolEnabled = this.dataset.currencySymbolEnabled === 'true';
    const currencyIsoCode = this.dataset.currencyIsoCode || 'EUR';

    const numberOnly = () =>
      new Intl.NumberFormat(document.documentElement.lang || undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);

      // Use Shopify's money format if available
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      if (currencyCodeEnabled && currencySymbolEnabled) {
        const format = this.dataset.moneyFormatWithCurrency || window.theme?.moneyWithCurrencyFormat || '€{{amount_with_comma_separator}} EUR';
        return Shopify.formatMoney(cents, format);
      }

      if (!currencyCodeEnabled && currencySymbolEnabled) {
        const format = this.dataset.moneyFormat || window.theme?.moneyFormat || '€{{amount_with_comma_separator}}';
        return Shopify.formatMoney(cents, format);
      }

      if (currencyCodeEnabled && !currencySymbolEnabled) {
        return `${numberOnly()} ${currencyIsoCode}`;
      }

      return numberOnly();
      }

    // Fallback for non-Shopify.formatMoney environments
    const numeric = numberOnly();
    if (currencyCodeEnabled && !currencySymbolEnabled) return `${numeric} ${currencyIsoCode}`;
    if (!currencyCodeEnabled && !currencySymbolEnabled) return numeric;

    const currencyDisplay = currencyCodeEnabled ? 'code' : 'symbol';
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currencyIsoCode,
      currencyDisplay
    }).format(cents / 100);
    }

    getSelectedVariants() {
      const selected = [];

      this.checkboxes.forEach(checkbox => {
        if (checkbox.checked) {
          selected.push({
            id: parseInt(checkbox.dataset.variantId, 10),
            quantity: 1
          });
        }
      });

      return selected;
    }

    async onFormSubmit(event, form) {
      const selectedVariants = this.getSelectedVariants();

      // If no complementary products selected, let normal form submission proceed
      if (selectedVariants.length === 0) {
        return;
      }

      // Prevent normal form submission
      event.preventDefault();
      event.stopPropagation();

      // Get the main product variant ID from the form
      const mainVariantInput = form.querySelector('[name="id"]');
      const mainVariantId = mainVariantInput ? parseInt(mainVariantInput.value, 10) : parseInt(this.mainVariantId, 10);
      const mainQuantityInput = form.querySelector('[name="quantity"]');
      const mainQuantity = mainQuantityInput ? parseInt(mainQuantityInput.value, 10) : 1;

      // Build items array - main product first, then selected complementary products
      const items = [
        { id: mainVariantId, quantity: mainQuantity },
        ...selectedVariants
      ];

      // Show loading state
      const submitButton = form.querySelector('[type="submit"]');
      this.setButtonLoading(submitButton, true);

      try {
        const response = await this.addItemsToCart(items);

        // Trigger cart update events with the response
        this.triggerCartUpdate(response);

        // Keep checkboxes checked - don't reset after adding to cart

      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.handleError(error, form);
      } finally {
        this.setButtonLoading(submitButton, false);
      }
    }

    async addItemsToCart(items) {
      // Get sections to render for cart drawer/notification/items
      let sections = [];
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartItems = document.querySelector('cart-items');

      // Initialize cart drawer if present (loads template content)
      if (cartDrawer && typeof cartDrawer.init === 'function') {
        cartDrawer.init();
      }

      // Initialize cart notification if present
      if (cartNotification && typeof cartNotification.init === 'function') {
        if (typeof window.loadTemplateContent === 'function') {
          window.loadTemplateContent(cartNotification);
        }
      }

      // Collect sections from cart drawer
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        try {
          const drawerSections = cartDrawer.getSectionsToRender().map(s => s.id);
          sections = [...sections, ...drawerSections];
        } catch (e) { /* ignore */ }
      }

      // Collect sections from cart notification
      if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        try {
          const notifSections = cartNotification.getSectionsToRender().map(s => s.id);
          sections = [...sections, ...notifSections];
        } catch (e) { /* ignore */ }
      }

      // Collect sections from cart items
      if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
        try {
          const itemsSections = cartItems.getSectionsToRender().map(s => s.section);
          sections = [...sections, ...itemsSections];
        } catch (e) { /* ignore */ }
      }

      // Remove duplicates
      sections = [...new Set(sections)];

      const body = { items };
      if (sections.length > 0) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      return response.json();
    }

    triggerCartUpdate(response) {
      // Dispatch cart update events (use theme's PUB_SUB_EVENTS if available)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'complementary-bundle',
          cartData: response
        });
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));

      // Handle cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartItems = document.querySelector('cart-items');

      // Determine action based on theme settings
      const atcAction = window.theme?.settings?.cartDrawer?.atcAction || 'drawer';

      if (atcAction === 'page') {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }

      // Initialize and render cart drawer
      if (cartDrawer) {
        // Initialize the drawer first (loads template content)
        if (typeof cartDrawer.init === 'function') {
          cartDrawer.init();
        }

        // Remove is-empty class if present
        if (cartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        }

        // Render contents if we have sections data
        if (response && response.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
        } else if (typeof cartDrawer.reload === 'function') {
          // Fallback: reload the drawer content
          cartDrawer.reload();
          // Open after reload completes
          if (atcAction === 'drawer') {
            setTimeout(() => {
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }, 300);
          }
        }
      }

      // Handle cart items (for cart page)
      if (cartItems && response && response.sections && typeof cartItems.renderContents === 'function') {
        cartItems.renderContents(response);
      }

      // Handle cart notification
      if (cartNotification && atcAction === 'notification') {
        if (typeof cartNotification.init === 'function') {
          cartNotification.init();
        }
        if (response && response.sections && typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(response);
        }
      }

      // Update cart count
      this.updateCartCount();
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update all cart count indicators
        document.querySelectorAll('.cart-count-bubble, [data-cart-count], .cart-count').forEach(el => {
          const countSpan = el.querySelector('span') || el;
          if (cart.item_count > 0) {
            el.classList.remove('hidden');
            countSpan.textContent = cart.item_count;
          } else {
            el.classList.add('hidden');
          }
        });
      } catch (e) {
        console.error('Error updating cart count:', e);
      }
    }

    resetCheckboxes() {
      this.checkboxes.forEach(checkbox => {
        checkbox.checked = false;
        const item = checkbox.closest('.complementary-bundle__item');
        item?.classList.remove('complementary-bundle__item--selected');
      });

      // Reset prices to original
      this.mainPrice = this.originalMainPrice;
      this.mainComparePrice = this.originalMainComparePrice;
      this.updateProductPrice();
    }

    setButtonLoading(button, isLoading) {
      if (!button) return;

      if (isLoading) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      } else {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }

    handleError(error, form) {
      const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
      const errorMessage = form.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorWrapper.removeAttribute('hidden');
        errorMessage.textContent = error.message || 'Er is een fout opgetreden. Probeer het opnieuw.';
      } else {
        alert(error.message || 'Er is een fout opgetreden. Probeer het opnieuw.');
      }
    }
  }

  customElements.define('complementary-bundle', ComplementaryBundle);
}
